import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { CATEGORIES, CATEGORY_LABELS, formatDateTime } from "@/lib/format";
import { Rss, Plus, Trash2, RefreshCw, Power, Save, Archive, Zap } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
  { name: "Tagesschau", url: "https://www.tagesschau.de/index~rss2.xml" },
  { name: "ZEIT ONLINE", url: "https://newsfeed.zeit.de/index" },
  { name: "Spiegel", url: "https://www.spiegel.de/schlagzeilen/index.rss" },
];

export default function RssImport() {
  const [feeds, setFeeds] = useState([]);
  const [form, setForm] = useState({ name: "", url: "", category: "politik", interval_hours: 6, enabled: true });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [settings, setSettings] = useState({ auto_publish: false, per_day: 3 });
  const [backfill, setBackfill] = useState({ status: "idle", done: 0, total: 0 });

  const load = () => api.get("/admin/rss/feeds").then((r) => setFeeds(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/admin/rss/settings").then((r) => setSettings({ auto_publish: r.data.auto_publish, per_day: r.data.per_day }));
    api.get("/admin/rss/backfill/status").then((r) => setBackfill(r.data));
  }, []);
  useEffect(() => {
    if (backfill.status !== "running") return;
    const t = setInterval(() => api.get("/admin/rss/backfill/status").then((r) => setBackfill(r.data)), 3000);
    return () => clearInterval(t);
  }, [backfill.status]);

  const saveSettings = async (next) => {
    const merged = { ...settings, ...next };
    setSettings(merged);
    await api.put("/admin/rss/settings", merged);
    toast.success("Einstellungen gespeichert.");
  };
  const publishNow = async () => {
    const { data } = await api.post("/admin/rss/publish-now");
    toast.success(`${data.published} Artikel veröffentlicht.`);
  };
  const startBackfill = async () => {
    if (!window.confirm("30-Tage-Archiv mit KI erzeugen? Das dauert einige Minuten und veröffentlicht datierte Artikel.")) return;
    const { data } = await api.post("/admin/rss/backfill", { per_day: 3, days: 30 });
    setBackfill({ status: "running", done: 0, total: data.total });
    toast.success("Archiv-Aufbau gestartet.");
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.url.trim()) { toast.error("Name und URL erforderlich."); return; }
    setSaving(true);
    try {
      await api.post("/admin/rss/feeds", { ...form, interval_hours: Number(form.interval_hours) });
      toast.success("Feed hinzugefügt.");
      setForm({ name: "", url: "", category: "politik", interval_hours: 6, enabled: true });
      load();
    } catch { toast.error("Fehler beim Speichern."); }
    finally { setSaving(false); }
  };

  const toggle = async (f) => {
    await api.put(`/admin/rss/feeds/${f.id}`, { name: f.name, url: f.url, category: f.category, interval_hours: f.interval_hours, enabled: !f.enabled });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Feed wirklich löschen?")) return;
    await api.delete(`/admin/rss/feeds/${id}`); toast.success("Gelöscht."); load();
  };

  const fetchOne = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/rss/feeds/${id}/fetch`);
      toast.success(`${data.created} neue Entwürfe erstellt.`); load();
    } catch { toast.error("Abruf fehlgeschlagen."); }
    finally { setBusy(""); }
  };

  const fetchAll = async () => {
    setBusy("all");
    try {
      const { data } = await api.post("/admin/rss/fetch-all");
      toast.success(`${data.drafts_created} Entwürfe aus ${data.feeds_processed} Feeds.`); load();
    } catch { toast.error("Abruf fehlgeschlagen."); }
    finally { setBusy(""); }
  };

  const field = "w-full border border-edborder bg-white px-4 py-2.5 text-sm outline-none focus:border-prussian";
  const lbl = "block text-xs uppercase tracking-wide text-muted-foreground font-body mb-1.5 font-semibold";

  return (
    <AdminLayout title="RSS-Import">
      <div className="flex items-start justify-between gap-4 mb-6">
        <p className="text-sm text-muted-foreground font-body max-w-2xl">
          Automatische Aggregation: konfigurierte RSS-Feeds werden regelmäßig abgerufen und als <strong>Entwürfe</strong> in der Artikelliste angelegt – mit Titel, Text, Bild und Quelle, jederzeit bearbeitbar vor der Veröffentlichung. Nur im Redaktionssystem sichtbar.
        </p>
        <button data-testid="rss-fetch-all-btn" onClick={fetchAll} disabled={busy === "all"}
          className="inline-flex items-center gap-2 bg-prussian text-white font-body font-semibold text-sm px-5 py-2.5 hover:bg-prussian-dark disabled:opacity-50 whitespace-nowrap">
          <RefreshCw size={15} className={busy === "all" ? "animate-spin" : ""} /> Alle jetzt abrufen
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-edborder p-5" data-testid="rss-autopublish-panel">
          <h2 className="font-headline font-bold text-lg text-prussian mb-3">Automatische Veröffentlichung</h2>
          <label className="flex items-center gap-2 text-sm font-body text-charcoal">
            <input type="checkbox" data-testid="autopublish-toggle" checked={settings.auto_publish} onChange={(e) => saveSettings({ auto_publish: e.target.checked })} />
            Entwürfe automatisch veröffentlichen, verteilt auf die Rubriken
          </label>
          <div className="flex items-center gap-2 mt-3">
            <span className="text-sm font-body text-muted-foreground">Artikel pro Tag:</span>
            <input type="number" min="1" max="10" data-testid="perday-input" value={settings.per_day}
              onChange={(e) => setSettings({ ...settings, per_day: Number(e.target.value) })} onBlur={() => saveSettings({})}
              className="w-16 border border-edborder px-2 py-1 text-sm outline-none focus:border-prussian" />
          </div>
          <button onClick={publishNow} data-testid="publish-now-btn" className="mt-4 inline-flex items-center gap-2 border border-prussian text-prussian text-sm font-body px-4 py-2 hover:bg-prussian hover:text-white transition-colors">
            <Zap size={14} /> Jetzt veröffentlichen
          </button>
        </div>
        <div className="bg-white border border-edborder p-5" data-testid="rss-backfill-panel">
          <h2 className="font-headline font-bold text-lg text-prussian mb-3">Archiv aufbauen (30 Tage)</h2>
          <p className="text-xs text-muted-foreground font-body mb-3">Erzeugt eigenständige, datierte KI-Artikel zu realen Themen der letzten 30 Tage (ca. 90 Beiträge, auf alle Rubriken verteilt).</p>
          {backfill.status === "running" ? (
            <div>
              <div className="h-2 bg-offwhite border border-edborder"><div className="h-full bg-prussian transition-all" style={{ width: `${backfill.total ? Math.round((backfill.done / backfill.total) * 100) : 0}%` }} /></div>
              <p className="text-xs text-muted-foreground mt-2 font-body">{backfill.done} / {backfill.total} erstellt…</p>
            </div>
          ) : (
            <button onClick={startBackfill} data-testid="backfill-btn" className="inline-flex items-center gap-2 bg-prussian text-white text-sm font-body px-4 py-2 hover:bg-prussian-dark transition-colors">
              <Archive size={14} /> Archiv jetzt aufbauen
            </button>
          )}
          {backfill.status === "done" && backfill.total > 0 && <p className="text-xs text-green-700 mt-2 font-body">Archiv aufgebaut ({backfill.total} Artikel).</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-edborder overflow-x-auto" data-testid="rss-feeds-list">
          <table className="w-full text-sm font-body">
            <thead>
              <tr className="bg-prussian text-white text-left uppercase text-[11px] tracking-wide">
                <th className="px-4 py-3">Feed</th>
                <th className="px-4 py-3">Rubrik</th>
                <th className="px-4 py-3">Intervall</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-edborder">
              {feeds.map((f) => (
                <tr key={f.id} data-testid={`rss-feed-${f.id}`} className="hover:bg-offwhite">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-prussian">{f.name}</p>
                    <p className="text-[11px] text-muted-foreground truncate max-w-[220px]">{f.url}</p>
                  </td>
                  <td className="px-4 py-3">{f.category === "auto" ? "KI-Auto" : CATEGORY_LABELS[f.category]}</td>
                  <td className="px-4 py-3 whitespace-nowrap">alle {f.interval_hours} h</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-[11px] font-semibold px-2 py-1 uppercase tracking-wide ${f.enabled ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>
                      {f.enabled ? "Aktiv" : "Pausiert"}
                    </span>
                    <p className="text-[11px] text-muted-foreground mt-1">{f.last_status}{f.last_fetched ? ` · ${formatDateTime(f.last_fetched)}` : ""}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => fetchOne(f.id)} disabled={busy === f.id} data-testid={`rss-fetch-${f.id}`} title="Jetzt abrufen" className="w-8 h-8 flex items-center justify-center text-prussian border border-edborder hover:bg-offwhite disabled:opacity-40"><RefreshCw size={14} className={busy === f.id ? "animate-spin" : ""} /></button>
                      <button onClick={() => toggle(f)} data-testid={`rss-toggle-${f.id}`} title={f.enabled ? "Pausieren" : "Aktivieren"} className={`w-8 h-8 flex items-center justify-center border border-edborder hover:bg-offwhite ${f.enabled ? "text-green-700" : "text-muted-foreground"}`}><Power size={14} /></button>
                      <button onClick={() => remove(f.id)} data-testid={`rss-delete-${f.id}`} title="Löschen" className="w-8 h-8 flex items-center justify-center text-breaking border border-edborder hover:bg-breaking/10"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {feeds.length === 0 && <tr><td colSpan={5} className="text-center text-muted-foreground py-12">Noch keine Feeds konfiguriert.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-edborder p-6 h-fit">
          <h2 className="font-headline font-bold text-xl text-prussian mb-4 flex items-center gap-2"><Rss size={18} /> Feed hinzufügen</h2>
          <form onSubmit={create} data-testid="rss-create-form" className="space-y-4">
            <div><label className={lbl}>Name der Quelle</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} data-testid="rss-name-input" placeholder="z. B. Tagesschau" /></div>
            <div><label className={lbl}>RSS-URL</label><input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className={field} data-testid="rss-url-input" placeholder="https://…/rss.xml" /></div>
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map((p) => (
                <button type="button" key={p.name} onClick={() => setForm({ ...form, name: p.name, url: p.url })}
                  className="text-[11px] font-body border border-edborder px-2 py-1 text-prussian hover:bg-offwhite">{p.name}</button>
              ))}
            </div>
            <div><label className={lbl}>Ziel-Rubrik</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={field} data-testid="rss-category-select">
                <option value="auto">Automatisch (KI ordnet zu)</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
              </select>
            </div>
            <div><label className={lbl}>Abruf-Intervall (Stunden)</label>
              <input type="number" min="1" value={form.interval_hours} onChange={(e) => setForm({ ...form, interval_hours: e.target.value })} className={field} data-testid="rss-interval-input" />
            </div>
            <label className="flex items-center gap-2 text-sm font-body text-charcoal">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="rss-enabled-checkbox" /> Feed aktiv
            </label>
            <button type="submit" disabled={saving} data-testid="rss-create-btn" className="w-full inline-flex items-center justify-center gap-2 bg-prussian text-white font-body font-semibold text-sm py-2.5 hover:bg-prussian-dark disabled:opacity-50">
              <Plus size={15} /> {saving ? "Speichern…" : "Feed anlegen"}
            </button>
          </form>
          <p className="text-[11px] text-muted-foreground mt-4 font-body">Importierte Beiträge erscheinen als Entwurf in <Link to="/admin/artikel" className="underline">Artikel</Link>.</p>
        </div>
      </div>
    </AdminLayout>
  );
}
